import type { BillCorrectionPreview } from '../../types/bill';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';

type BillCorrectionPreviewModalProps = {
  isOpen: boolean;
  preview: BillCorrectionPreview | null;
  isConfirming?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

const formatDelta = (value: string) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n === 0) return formatCurrency(0);
  const prefix = n > 0 ? '+' : '';
  return `${prefix}${formatCurrency(n)}`;
};

const BillCorrectionPreviewModal = ({
  isOpen,
  preview,
  isConfirming = false,
  onConfirm,
  onCancel,
}: BillCorrectionPreviewModalProps) => {
  if (!isOpen || !preview) return null;

  const hasBlockers = preview.blockers.length > 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Cost correction preview</h3>
        <p className="mt-1 text-sm text-gray-600">
          Review inventory, COGS, and accounts payable impacts before saving.
        </p>

        {preview.blockers.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800 mb-1">Cannot proceed</p>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
              {preview.blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </div>
        )}

        {preview.warnings.length > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm font-medium text-amber-900 mb-1">Warnings</p>
            <ul className="list-disc list-inside text-sm text-amber-800 space-y-0.5">
              {preview.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">Bill total</span>
            <span className="font-medium text-gray-900 tabular-nums">
              {formatCurrency(preview.old_total_amount)} →{' '}
              {formatCurrency(preview.new_total_amount)}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">COGS change</span>
            <span className="font-medium tabular-nums">{formatDelta(preview.cogs_delta)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">Inventory change</span>
            <span className="font-medium tabular-nums">{formatDelta(preview.inventory_delta)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-600">A/P change</span>
            <span className="font-medium tabular-nums">{formatDelta(preview.ap_delta)}</span>
          </div>
        </div>

        {preview.affected_invoices.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Affected invoices</p>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {preview.affected_invoices.map((invoice) => (
                <div
                  key={invoice.invoice_id}
                  className="flex justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="text-gray-700">{invoice.invoice_number}</span>
                  <span className="tabular-nums font-medium">{formatDelta(invoice.cogs_delta)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {preview.line_impacts.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Line impacts</p>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
              {preview.line_impacts.map((impact, index) => (
                <div key={impact.bill_line_id ?? `impact-${index}`} className="px-3 py-2">
                  {impact.action === 'removed' ? (
                    <span className="text-gray-700">
                      Line removed — qty {formatPreviewQuantity(impact.old_qty ?? '0')} → 0
                      {impact.qty_returned
                        ? ` (${formatPreviewQuantity(impact.qty_returned)} returned)`
                        : ''}
                    </span>
                  ) : impact.action === 'added' ? (
                    <span className="text-gray-700">
                      Line added — qty {formatPreviewQuantity(impact.new_qty ?? '0')}
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      Qty {formatPreviewQuantity(impact.old_qty ?? '0')} →{' '}
                      {formatPreviewQuantity(impact.new_qty ?? '0')}
                      {impact.sold_qty ? ` (${formatPreviewQuantity(impact.sold_qty)} sold)` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            disabled={isConfirming}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming || hasBlockers}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isConfirming ? 'Saving...' : 'Confirm correction'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillCorrectionPreviewModal;
